import { errorDev } from '../utils/logger.js';
const DEFAULT_TIMEOUT_MS = 12000;
const DEFAULT_RETRIES = 1;
const DEFAULT_RETRY_DELAY_MS = 400;

export class AppRequestError extends Error {
  constructor(message, {
    code = "REQUEST_FAILED",
    status = null,
    operation = "unknown",
    retryable = false,
    cause = null,
    details = null,
  } = {}) {
    super(message);
    this.name = "AppRequestError";
    this.code = code;
    this.status = status;
    this.operation = operation;
    this.retryable = retryable;
    this.cause = cause;
    this.details = details;
  }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isFetchRetryable = (error) => {
  if (!error) return false;
  if (error.name === "AbortError") return true;
  if (error instanceof TypeError) return true;
  if (error.status === 408 || error.status === 429) return true;
  if (error.status >= 500) return true;
  return false;
};

export async function runWithRetry(operation, {
  operationName = "operation",
  retries = DEFAULT_RETRIES,
  retryDelayMs = DEFAULT_RETRY_DELAY_MS,
  isRetryable = () => false,
} = {}) {
  let lastError = null;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      if (attempt >= retries || !isRetryable(error)) break;
      const backoff = retryDelayMs * Math.pow(2, attempt);
      await sleep(backoff);
    }
  }

  if (lastError instanceof AppRequestError) {
    throw lastError;
  }

  throw new AppRequestError(
    lastError?.message || `${operationName} failed`,
    {
      code: "OPERATION_FAILED",
      operation: operationName,
      retryable: false,
      cause: lastError,
    }
  );
}

export async function requestJson(url, {
  method = "GET",
  headers = {},
  body,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  retries = DEFAULT_RETRIES,
  retryDelayMs = DEFAULT_RETRY_DELAY_MS,
  operationName = "requestJson",
  signal,
} = {}) {
  return runWithRetry(async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);

    if (signal) {
      signal.addEventListener("abort", () => controller.abort(), { once: true });
    }

    try {
      const response = await fetch(url, {
        method,
        headers,
        body,
        signal: controller.signal,
      });

      let parsed = null;
      try {
        parsed = await response.clone().json();
      } catch (err) {
        errorDev("Operation failed:", err);
        parsed = null;
      }

      if (!response.ok) {
        throw new AppRequestError(
          `HTTP ${response.status} for ${operationName}`,
          {
            code: "HTTP_ERROR",
            status: response.status,
            operation: operationName,
            retryable: response.status === 408 || response.status === 429 || response.status >= 500,
            details: parsed,
          }
        );
      }

      return parsed;
    } catch (error) {
      if (error instanceof AppRequestError) {
        throw error;
      }
      if (error?.name === "AbortError") {
        throw new AppRequestError(
          `${operationName} timed out`,
          {
            code: "TIMEOUT",
            operation: operationName,
            retryable: true,
            cause: error,
          }
        );
      }
      throw new AppRequestError(
        error?.message || `${operationName} network failure`,
        {
          code: "NETWORK_ERROR",
          operation: operationName,
          retryable: true,
          cause: error,
        }
      );
    } finally {
      clearTimeout(timeout);
    }
  }, {
    operationName,
    retries,
    retryDelayMs,
    isRetryable: isFetchRetryable,
  });
}

export function normalizeFirestoreError(error, operationName = "firestore") {
  const code = error?.code || "unknown";
  const message = String(error?.message || "");
  const isInternalAssertion = message.toLowerCase().includes("internal assertion failed");
  const isQuotaExceeded = error?.status === 429 || /quota|rate limit/i.test(message);
  const retryableCodes = new Set([
    "aborted",
    "cancelled",
    "deadline-exceeded",
    "resource-exhausted",
    "unavailable",
  ]);

  return new AppRequestError(
    error?.message || `${operationName} failed`,
    {
      code: `FIRESTORE_${String(code).toUpperCase().replace(/[^A-Z0-9]+/g, "_")}`,
      operation: operationName,
      retryable: !isQuotaExceeded && (retryableCodes.has(code) || isInternalAssertion),
      cause: error,
      details: { firestoreCode: code, isInternalAssertion },
    }
  );
}
